import { Loader2, Table2 } from "lucide-react";
import { useEffect, useState } from "react";

import {
  CardActionButton,
  DisconnectFooter,
  IntegrationCardShell,
  StatusPill,
} from "@/components/dashboard/IntegrationCardShell";

const STALE_CONNECTING_MS = 30 * 1000; // 30 seconds

interface GoogleSheetsIntegrationPanelProps {
  integration:
    | {
        connectedAt?: number;
        status: string;
        lastError?: string;
        config?: unknown;
      }
    | null
    | undefined;
  onStartOAuth: () => Promise<unknown>;
  onDisconnect: () => Promise<unknown>;
}

export function GoogleSheetsIntegrationPanel({
  integration,
  onStartOAuth,
  onDisconnect,
}: GoogleSheetsIntegrationPanelProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);

  const currentStatus = integration?.status;
  const connectingAt = integration?.connectedAt;
  const config = integration?.config as { redirectUrl?: string } | null;

  // Force re-render when stale timeout elapses
  const [, setTick] = useState(0);
  useEffect(() => {
    if (currentStatus !== "connecting" || connectingAt == null) return;
    const remaining = STALE_CONNECTING_MS - (Date.now() - connectingAt);
    if (remaining <= 0) return;
    const timer = setTimeout(() => setTick((t) => t + 1), remaining + 100);
    return () => clearTimeout(timer);
  }, [currentStatus, connectingAt]);

  // Redirect to Composio OAuth when redirectUrl appears
  useEffect(() => {
    if (currentStatus === "connecting" && config?.redirectUrl && isSubmitting) {
      window.location.href = config.redirectUrl;
    }
  }, [currentStatus, config?.redirectUrl, isSubmitting]);

  async function handleConnect() {
    setIsSubmitting(true);
    try {
      await onStartOAuth();
      // redirectUrl will arrive via Convex subscription → triggers redirect in useEffect
    } catch {
      setIsSubmitting(false);
    }
  }

  async function handleDisconnect() {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
      setShowDisconnectConfirm(false);
    } catch (err) {
      console.error("Google Sheets disconnect failed:", err);
    } finally {
      setIsDisconnecting(false);
    }
  }

  const isStaleConnecting =
    currentStatus === "connecting" &&
    connectingAt != null &&
    Date.now() - connectingAt > STALE_CONNECTING_MS;

  // Disconnected / null / stale connecting
  if (!integration || currentStatus === "disconnected" || isStaleConnecting) {
    return (
      <IntegrationCardShell
        icon={Table2}
        title="Google Sheets"
        description="Read, create, and edit spreadsheets"
        footer={
          <CardActionButton tone="tonal" disabled={isSubmitting} onClick={handleConnect}>
            {isSubmitting ? "Connecting…" : "Connect"}
          </CardActionButton>
        }
      />
    );
  }

  // Connecting
  if (currentStatus === "connecting") {
    return (
      <IntegrationCardShell
        icon={Table2}
        title="Google Sheets"
        pill={<StatusPill variant="pending">Connecting…</StatusPill>}
        description="Read, create, and edit spreadsheets"
        footer={
          <div className="flex items-center justify-center gap-2 py-2.5 font-body text-sm text-home-ink/55 dark:text-white/55">
            <Loader2 className="h-4 w-4 animate-spin" />
            Waiting for Google authorization
          </div>
        }
      />
    );
  }

  // Error — human message only; the raw error stays in Convex logs
  if (currentStatus === "error") {
    return (
      <IntegrationCardShell
        icon={Table2}
        title="Google Sheets"
        pill={<StatusPill variant="attention">Action needed</StatusPill>}
        description="Your Google Sheets connection isn't working. Reconnect to keep it running."
        footer={
          <CardActionButton tone="primary" disabled={isSubmitting} onClick={handleConnect}>
            {isSubmitting ? "Connecting…" : "Reconnect"}
          </CardActionButton>
        }
      />
    );
  }

  // Connected
  const connectedLabel = integration?.connectedAt
    ? `Connected · ${new Date(integration.connectedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : "Connected";

  return (
    <IntegrationCardShell
      icon={Table2}
      title="Google Sheets"
      pill={<StatusPill variant="active">{connectedLabel}</StatusPill>}
      description="Read, create, and edit spreadsheets"
      footer={
        <DisconnectFooter
          confirming={showDisconnectConfirm}
          disconnecting={isDisconnecting}
          onConfirmChange={setShowDisconnectConfirm}
          onDisconnect={handleDisconnect}
        />
      }
    />
  );
}
